import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { DEFAULT_TRANSLATIONS } from '@/lib/translations';
import { pickInitialLocale } from '@/lib/localePreference';

export interface SupportedLanguage {
  code: string;
  name: string;
  native_name: string;
  enabled: boolean;
  sort_order: number;
}

interface UseTranslationOptions {
  clubId?: string | null;
  defaultLocale?: string;
  /** Bumped by the DB (club_settings.translations_updated_at) whenever the
   *  club's translation ledger changes. A change after the first load
   *  triggers a debounced refetch of the current locale's bundle so open fan
   *  pages pick up edits without a reload. */
  version?: string | null;
  /** The kiosk serving the page, when it has no club of its own: its menu is
   *  translated under the kiosk, and those rows merge under the club's. */
  operatorId?: string | null;
}

/**
 * Shape of the `t()` function, for components that receive it as a prop
 * instead of running their own hook instance. The fan page must have exactly
 * ONE locale: independent hook instances (no clubId/defaultLocale) each pick
 * their own language, which made the match section render English while the
 * rest of the page was Finnish.
 */
export type TranslateFn = (key: string, params?: Record<string, string>) => string;

export function useTranslation({ clubId, operatorId = null, defaultLocale, version }: UseTranslationOptions = {}) {
  const getStorageKeys = useCallback(() => {
    return {
      lang: clubId ? `fan_language_${clubId}` : 'fan_language',
      manual: clubId ? `fan_language_is_manual_${clubId}` : 'fan_language_is_manual'
    };
  }, [clubId]);

  const [locale, setLocale] = useState<string>(() => {
    const keys = {
      lang: clubId ? `fan_language_${clubId}` : 'fan_language',
      manual: clubId ? `fan_language_is_manual_${clubId}` : 'fan_language_is_manual'
    };
    const isManual = localStorage.getItem(keys.manual) === 'true';
    const stored = localStorage.getItem(keys.lang);
    return (isManual && stored) || defaultLocale || localStorage.getItem('fan_language') || 'en';
  });

  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [languages, setLanguages] = useState<SupportedLanguage[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Sync locale once club context resolves: a manual choice stored for THIS
  // club wins; otherwise the club default applies. The initial state above
  // runs while clubId is still null (it reads the GLOBAL keys), so without the
  // manual-restore branch a fan's saved per-club language was never applied —
  // e.g. picked FI for club B, later EN for club A (updating the global key),
  // returned to club B → stuck in EN despite the stored FI preference.
  useEffect(() => {
    const keys = getStorageKeys();
    const isManual = localStorage.getItem(keys.manual) === 'true';
    const stored = localStorage.getItem(keys.lang);

    // Only restore a manual choice the club still offers (or while languages
    // haven't loaded yet). Without this gate, a stored language the club later
    // disabled ping-pongs forever against the validation effect below — restore
    // sets it, validation rejects it, restore sets it again — an infinite
    // setLocale loop that hammers the translations fetch and freezes the page.
    const storedIsOffered =
      !!stored && (languages.length === 0 || languages.some(l => l.code === stored));

    if (isManual && stored && storedIsOffered) {
      if (stored !== locale) setLocale(stored);
    } else if (defaultLocale && defaultLocale !== locale) {
      setLocale(defaultLocale);
    }
  }, [defaultLocale, locale, getStorageKeys, clubId, languages]);

  // First visit: honour the browser's language once the club's languages
  // have loaded, instead of silently landing on the club default. Runs at
  // most once per mount (autoPicked ref) and only while the fan has no
  // stored choice for this club — never overrides a manual pick.
  //
  // Gated on `clubId` being resolved (not just `languages` being non-empty):
  // TagPage's clubId comes from an async fetch and starts null, while this
  // hook's OWN "load available languages" effect fetches the global
  // supported_languages universe regardless of clubId and can resolve
  // first. Without this gate, the one-shot pick could fire once against
  // that unrestricted universe (and the global, non-club-scoped storage
  // keys) before the real club-scoped language list ever arrives — and,
  // being one-shot, never get a second chance to run against the right set.
  const autoPicked = useRef(false);

  // Validate locale against available languages for this club
  useEffect(() => {
    if (languages.length === 0) return;

    if (!autoPicked.current && clubId) {
      autoPicked.current = true;
      const keys = getStorageKeys();
      const hasManualChoice = localStorage.getItem(keys.manual) === 'true';
      if (!hasManualChoice && typeof navigator !== 'undefined') {
        const picked = pickInitialLocale({
          manualChoice: null,
          navigatorLanguages: navigator.languages ?? [navigator.language],
          enabled: languages.map(l => l.code),
          clubDefault: defaultLocale ?? null,
        });
        if (picked !== locale) {
          // Only pin this as a manual choice when it actually diverges from
          // the admin-configured default. A pure fallback TO that default
          // (e.g. the browser's language isn't offered) doesn't need
          // pinning — the restore effect above already wants defaultLocale
          // too, so there's no ping-pong to prevent — and leaving it
          // unpinned means the fan keeps following future
          // default_language_code changes instead of being stuck on
          // today's value forever. When it DOES diverge, persist it through
          // the same keys `changeLanguage` writes (without touching that
          // function) so the restore effect treats this pick as established
          // and doesn't revert it back to the club default on its next run
          // — the ping-pong the restore effect's own comment warns about,
          // just between it and this effect instead of between it and the
          // validation below.
          if (picked !== (defaultLocale ?? null)) {
            localStorage.setItem(keys.lang, picked);
            localStorage.setItem(keys.manual, 'true');
          }
          setLocale(picked);
          return; // let this effect re-run against the newly picked locale
        }
      }
    }

    if (!languages.some(l => l.code === locale)) {
      if (defaultLocale && languages.some(l => l.code === defaultLocale)) {
        setLocale(defaultLocale);
      } else {
        // Fallback to first available or 'en'
        const fallback = languages[0].code;
        setLocale(fallback);
      }
    }
  }, [languages, locale, defaultLocale, getStorageKeys, clubId]);

  // Load available languages
  useEffect(() => {
    let cancelled = false;

    const loadLanguages = async () => {
      // Fetch the full universe of enabled languages once
      const { data: universe } = await supabase
        .from('supported_languages')
        .select('*')
        .eq('enabled', true)
        .order('sort_order', { ascending: true });

      if (cancelled) return;
      const allEnabled: SupportedLanguage[] = universe || [];

      // If a club is identified, restrict to the codes that club has explicitly enabled.
      // An empty club_languages set (not a query error) means "nothing configured" — fall back to all.
      if (clubId) {
        const { data: clubLangs, error: clubLangsError } = await supabase
          .from('club_languages')
          .select('language_code')
          .eq('club_id', clubId);

        if (cancelled) return;

        // Only restrict when the query succeeded AND the club has at least one row.
        // On error, keep the universe so the fan can still pick a language.
        if (!clubLangsError && clubLangs && clubLangs.length > 0) {
          const enabledCodes = new Set(clubLangs.map(c => c.language_code));
          setLanguages(allEnabled.filter(l => enabledCodes.has(l.code)));
          return;
        }
      }

      setLanguages(allEnabled);
    };

    loadLanguages();

    return () => {
      cancelled = true;
    };
  }, [clubId]);

  // Bumped (debounced) when `version` changes after the first load, to force
  // the translations effect below to refetch even though locale/clubId did
  // not change. See the version-watch effect further down.
  const [reloadTick, setReloadTick] = useState(0);

  // Body of the translations-loading effect, extracted so both a
  // locale/clubId change AND a reloadTick bump can drive the same fetch.
  const loadTranslations = useCallback(async (
    currentLocale: string,
    currentClubId: string | null | undefined,
    currentOperatorId: string | null | undefined,
    cancelledRef: { current: boolean }
  ) => {
    // 0. Load from cache first for instant feedback
    const cacheKey = `translations_${currentLocale}_${currentClubId || 'core'}${currentOperatorId ? `_${currentOperatorId}` : ''}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        setTranslations(JSON.parse(cached));
        setIsLoading(false);
      } catch (e) {
        console.warn('Failed to parse cached translations', e);
      }
    } else {
      setIsLoading(true);
    }

    const mergedTranslations: Record<string, string> = {};

    try {
      // 1. Fetch core translations for current locale
      const { data: coreData } = await supabase
        .from('core_translations')
        .select('translation_key, translation_value')
        .eq('locale', currentLocale);
      if (cancelledRef.current) return;

      if (coreData) {
        coreData.forEach(t => {
          mergedTranslations[t.translation_key] = t.translation_value;
        });
      }

      // 2. The serving kiosk's rows (its menu, pick-up points, notices), when
      //    a kiosk without a club serves the page. Merged BEFORE the club's so
      //    a tenant may hand-correct a caterer string on its own page.
      if (currentOperatorId) {
        // operator_id is newer than the generated types (regenerated in the
        // clean-up phase), hence the untyped client.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: operatorData } = await (supabase as any)
          .from('club_translations')
          .select('translation_key, translation_value')
          .eq('operator_id', currentOperatorId)
          .eq('locale', currentLocale);
        if (cancelledRef.current) return;

        for (const t of (operatorData as { translation_key: string; translation_value: string }[] | null) ?? []) {
          mergedTranslations[t.translation_key] = t.translation_value;
        }
      }

      // 3. Fetch club-specific overrides if clubId is provided
      if (currentClubId) {
        const { data: clubData } = await supabase
          .from('club_translations')
          .select('translation_key, translation_value')
          .eq('club_id', currentClubId)
          .eq('locale', currentLocale);
        if (cancelledRef.current) return;

        if (clubData) {
          clubData.forEach(t => {
            mergedTranslations[t.translation_key] = t.translation_value;
          });
        }
      }

      setTranslations(mergedTranslations);
      // Persist to cache (harmless if a newer effect superseded us — the key
      // is scoped per locale+club, so we never poison another pair's cache)
      localStorage.setItem(cacheKey, JSON.stringify(mergedTranslations));
    } catch (err) {
      console.error('Error loading translations:', err);
    } finally {
      if (!cancelledRef.current) setIsLoading(false);
    }
  }, []);

  // Load translations based on current locale. Cancellation matters: on a
  // cold load this effect fires several times in quick succession (clubId
  // null → uuid, locale en → club default), and without it the LAST RESPONSE
  // to resolve — not the last effect to run — won the setTranslations race,
  // leaving the page in a random language per load. reloadTick is in the
  // deps so a version bump (see below) re-runs this without a locale/clubId
  // change.
  useEffect(() => {
    const cancelledRef = { current: false };

    loadTranslations(locale, clubId, operatorId, cancelledRef);

    return () => {
      cancelledRef.current = true;
    };
  }, [locale, clubId, operatorId, reloadTick, loadTranslations]);

  // Refetch (debounced) when the club's translation ledger changes elsewhere
  // (portal edit, auto-translate run) and TagPage's club_settings realtime
  // merge bumps `version`. Skipped on the first render — version already
  // reflects "no pending change" then — so this never causes an extra fetch
  // on mount.
  const lastVersion = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (lastVersion.current === undefined) { lastVersion.current = version ?? null; return; } // first render: no refetch
    if ((version ?? null) === lastVersion.current) return;
    lastVersion.current = version ?? null;
    const t = setTimeout(() => setReloadTick(n => n + 1), 750);
    return () => clearTimeout(t);
  }, [version]);

  // Translation function with fallback to English defaults
  const t = useCallback((key: string, params?: Record<string, string>): string => {
    let value = translations[key] || DEFAULT_TRANSLATIONS[key] || params?.defaultValue || key;

    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        value = value.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v);
      });
    }

    return value;
  }, [translations]);

  // Change language
  const changeLanguage = useCallback((newLocale: string) => {
    const keys = getStorageKeys();
    localStorage.setItem(keys.lang, newLocale);
    localStorage.setItem(keys.manual, 'true');
    // Also update global preference as a fallback for other clubs
    localStorage.setItem('fan_language', newLocale);
    setLocale(newLocale);
  }, [getStorageKeys]);

  return {
    t,
    locale,
    changeLanguage,
    languages,
    isLoading,
  };
}
